import React, { useMemo, useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import * as Yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, formatISO, parse } from 'date-fns';
import {
 Heading,
 Button,
 Flex,
 Modal,
 ModalContent,
 ModalOverlay,
 Grid,
 FormLabel,
 Box,
 InputGroup,
 Input,
 InputRightElement,
 Image,
 useToast,
} from '@chakra-ui/react';
import baseService from '../../services/base';
import { SuccessModal } from '../Modals';
import { ptBR } from 'date-fns/locale';
import { Translator } from '../Language';
import { Select } from '../Input';
import { fetchData } from '../../services/fetch';
import { calendarInput } from '../../assets/new-images/icons';

registerLocale('pt-BR', ptBR);

const schema = Yup.object().shape({
 startDate: Yup.date().required(<Translator path={'global.error.required'} />),
 deadline: Yup.date()
  .required(<Translator path={'global.error.required'} />)
  .min(Yup.ref('startDate'), 'A data final deve ser posterior à data inicial'),
});

export default function SurveySelectorModal({
 onClose,
 isOpen,
 id_company,
 editForm,
 groupSurveys,
}) {
 const toast = useToast();
 const { mutate } = useSWRConfig();
 const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
 const [success, setSuccess] = useState(false);

 const defaultValues = {
  startDate: editForm?.link_survey_company_reference_month_year
   ? parse(
      editForm?.link_survey_company_reference_month_year,
      "yyyy-MM-dd'T'HH:mm:ss.SSSX",
      new Date()
     )
   : undefined,
  deadline: editForm?.link_survey_company_end_application_date
   ? parse(
      editForm.link_survey_company_end_application_date,
      "yyyy-MM-dd'T'HH:mm:ss.SSSX",
      new Date()
     )
   : undefined,
 };

 const {
  control,
  reset,
  handleSubmit,
  watch,
  formState: { isValid, isDirty, isSubmitting },
 } = useForm({
  resolver: yupResolver(schema),
  defaultValues,
 });

 const startDate = watch('startDate');

 const { data: questionnairesList, isLoading } = useSWR(
  isOpen ? '/survey' : null,
  fetchData
 );

 const questionnaireOptions = useMemo(() => {
  const existingQuestionnairesIds = groupSurveys?.map((cert) => cert.s_id_survey) || [];
  const filtered = questionnairesList?.filter(
   (questionnaire) => !existingQuestionnairesIds.includes(questionnaire.id_survey)
  ) || [];

  const grouped = filtered.reduce((acc, questionnaire) => {
   const key = `${questionnaire.description}__${questionnaire.exercise ?? ''}`;
   if (!acc[key] || questionnaire.id_survey > acc[key].id_survey) {
    acc[key] = questionnaire;
   }
   return acc;
  }, {});

  return Object.values(grouped).map((questionnaire) => ({
   label: `${questionnaire.description}${questionnaire.exercise ? ` (${questionnaire.exercise})` : ''}`,
   value: questionnaire.id_survey,
  }));
 }, [questionnairesList, groupSurveys]);

 const handleQuestionnaireSelect = (value) => {
  setSelectedQuestionnaire(value);
 };

 const showErrorToast = (message) => {
  toast({
   title: 'Erro',
   description: message,
   status: 'error',
  });
 };

 async function onSubmit(data) {
  try {
   const { startDate, deadline } = data;
   const reference_month_year = formatISO(startDate);
   const end_application_date = formatISO(deadline);
   if (editForm) {
    await baseService.patch(
     `/link-survey-company/${editForm.link_survey_company_id_link_survey_company}`,
     {
      end_application_date,
      reference_month_year,
     }
    );
   } else {
/*     await baseService.post('/link-survey-company', {
     id_survey: selectedQuestionnaire,
     id_company,
     end_application_date,
     reference_month_year,
    }); */
        await baseService.post('/link-survey-company', {
     id_survey: selectedQuestionnaire,
     id_company,
     end_application_date,
     reference_month_year,
    });
   }
   
   mutate(`/company/linkSurveyCompany/${id_company}`);
   setSuccess(true);
  } catch (err) {
   showErrorToast(
    'Não foi possível adicionar o questionário. Tente novamente mais tarde.'
   );
  }
 }

 const handleClose = () => {
  onClose();
  reset();
  setSuccess(false);
 };

 const isButtonDisabled = editForm
  ? !isValid || !isDirty
  : !isValid || !selectedQuestionnaire;

 if (isLoading) {
  return null;
 }

 return success ? (
  <SuccessModal
   isOpen={isOpen}
   onClose={handleClose}
   title={`Questionário ${editForm ? 'editado' : 'adicionado'} com sucesso`}
  />
 ) : (
  <Modal isOpen={isOpen} onClose={handleClose} size='3xl' autoFocus={false}>
   <ModalOverlay />
   <ModalContent
    as='form'
    onSubmit={handleSubmit(onSubmit)}
    width='640px'
    borderRadius='12px'
    p='32px'
   >
    <Heading as='h3' fontSize='24px' lineHeight='28.18px' mb='32px'>
     {editForm ? 'Editar questionário' : 'Adicionar questionário'}
    </Heading>

    <Select
     label={editForm ? 'Questionário selecionado' : 'Questionário'}
     placeholder={
      editForm
       ? editForm.s_description
       : questionnaireOptions?.length === 0
       ? 'Nenhum questionário disponível'
       : 'Selecione um questionário'
     }
     options={questionnaireOptions}
     onChange={(e) => handleQuestionnaireSelect(e.target.value)}
     isDisabled={editForm ? true : false}
     h='50px'
     labelFontSize='12px'
    />

    <Grid templateColumns='repeat(2, 1fr)' gap={4} mt='6' mb='32px'>
     <Box>
      <FormLabel htmlFor='startDate' fontSize='12px'>
       Prazo inicial
      </FormLabel>
      <Controller
       name='startDate'
       control={control}
       render={({ field }) => (
        <InputGroup display='grid' isolation='unset'>
         <DatePicker
          {...field}
          selected={field.value}
          onChange={(date) => field.onChange(date)}
          minDate={new Date()}
          dateFormat='dd/MM/yyyy'
          locale='pt-BR'
          placeholderText='Selecione a data inicial'
          autoComplete='off'
          customInput={
           <Input
            value={field?.value}
            aria-label='Selecione'
            autoComplete='off'
            _autofill={{
             bg: '#F5F5FB !important',
             WebkitBoxShadow: '0 0 0px 1000px #F5F5FB inset',
             WebkitTextFillColor: '#000000',
             transition: 'background-color 5000s ease-in-out 0s',
            }}
            type='text'
            _focusVisible={{
             borderColor: 'inherit',
            }}
            _disabled={{
             bg: '#E5E5E5',
             borderColor: '#DCCFEC',
             cursor: 'not-allowed',
            }}
            readOnly
           />
          }
         />
         <InputRightElement
          pointerEvents='none'
          display={'flex'}
          alignItems='center'
          h='100%'
          cursor='pointer'
         >
          <Image src={calendarInput} alt='calendar' mr='10px' height='23px' />
         </InputRightElement>
        </InputGroup>
       )}
      />
     </Box>

     <Box>
      <FormLabel htmlFor='deadline' fontSize='12px'>
       Prazo final
      </FormLabel>
      <Controller
       name='deadline'
       control={control}
       render={({ field }) => (
        <InputGroup display='grid' isolation='unset'>
         <DatePicker
          {...field}
          selected={field.value}
          onChange={(date) => field.onChange(date)}
          minDate={startDate || new Date()}
          dateFormat='dd/MM/yyyy'
          locale='pt-BR'
          placeholderText='Selecione a data final'
          autoComplete='off'
          disabled={!startDate}
          customInput={
           <Input
            value={field?.value}
            aria-label='Selecione'
            autoComplete='off'
            _autofill={{
             bg: '#F5F5FB !important',
             WebkitBoxShadow: '0 0 0px 1000px #F5F5FB inset',
             WebkitTextFillColor: '#000000',
             transition: 'background-color 5000s ease-in-out 0s',
            }}
            type='text'
            _focusVisible={{
             borderColor: 'inherit',
            }}
            _disabled={{
             bg: '#E5E5E5',
             borderColor: '#DCCFEC',
             cursor: 'not-allowed',
            }}
            readOnly
           />
          }
         />
         <InputRightElement
          pointerEvents='none'
          display={'flex'}
          alignItems='center'
          h='100%'
          cursor='pointer'
         >
          <Image src={calendarInput} alt='calendar' mr='10px' height='23px' />
         </InputRightElement>
        </InputGroup>
       )}
      />
     </Box>
    </Grid>

    <Flex gap='24px' justify={'space-between'} w='100%'>
     <Button variant='cancel' flex='1' height='55px' onClick={handleClose}>
      <Translator path={'global.modal.cancel'} />
     </Button>
     <Button
      isLoading={isSubmitting}
      isDisabled={isButtonDisabled}
      type='submit'
      flex='1'
      height='55px'
      variant='primary'
     >
      {editForm ? 'Salvar' : 'Adicionar'}
     </Button>
    </Flex>
   </ModalContent>
  </Modal>
 );
}
